<template>
    <form
        :class="[$style.search_form_input, props.short && $style.short]"
        role="search"
        @submit.prevent="onSubmit"
    >
        <label
            :class="$style.label"
            for="search"
        >
            <Icon
                name="search"
                :class="$style.icon"
            />
            <input
                id="search"
                v-model="query"
                type="search"
                :placeholder="props.placeholder"
                :class="$style.input"
                autocomplete="off"
                :style="{
                    '--digits': props.placeholder?.length || 0,
                }"
                inputmode="search"
                @input="onInput"
                @focus="$emit('focus')"
            />
        </label>

        <Button
            v-if="!props.short"
            :disabled="disabledButton"
            type="submit"
            interfaceTarget="search-submit"
            :class="$style.button_override"
        >
            {{ buttonText }}
        </Button>
    </form>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import Button from '@/components_new/Button.vue'
import Icon from '@/components_new/Icon/Icon.vue'

const props = defineProps<{
    placeholder?: string
    buttonText?: string
    short?: boolean
}>()

const emit = defineEmits<{ focus: []; 'update:modelValue': [value: string] }>()

const query = ref('')
const disabledButton = ref(true)

const isShort = computed(() => props.short)

watch(isShort, (value) => {
    if (value) {
        query.value = ''
        disabledButton.value = true
        emit('update:modelValue', query.value)
    }
})

function onSubmit() {
    disabledButton.value = true
    emit('update:modelValue', query.value)
}

function onInput(e: Event) {
    const target = e.target as HTMLInputElement
    disabledButton.value = query.value.trim().length <= 1
    // for clearing input
    if (target.value === '') {
        emit('update:modelValue', '')
    }
}
</script>

<style module>
.search_form_input {
    --bg: var(--carbon-400);
    --border-color-form: transparent;
    --text: var(--white);
    --icon-color: var(--white-40);
    display: flex;
    align-items: center;
    background: var(--bg);
    outline: 1px solid var(--border-color-form);
    border-radius: 1000px;
    padding: 4px 4px 4px 16px;
    box-sizing: border-box;
    width: 100%;
    flex: 1;
    position: relative;
    transition: box-shadow 0.3s;
}

.icon {
    width: 24px;
    height: 24px;
    color: var(--icon-color);
    flex: 0 0 auto;
    margin-right: 4rem;
}

.search_form_input:focus-within {
    --bg: transparent;
    --border-color-form: var(--purple-300);
}

.input {
    width: 100%;
    background: transparent;
    border: none;
    outline: none;
    color: var(--text);
    padding-right: 4px;
    font-size: var(--font-size-label-l);
    line-height: var(--font-size-label-l);
    letter-spacing: var(--letter-spacing-label-l);
    caret-color: var(--purple-400);
    box-sizing: content-box;
    height: 21px;
}

.search_form_input:hover {
    --border-color-form: var(--purple-300);
    box-shadow: 0 0 30px 0 rgba(163, 109, 253, 0.64) inset;
}

.search_form_input:focus-within .icon {
    --icon-color: var(--purple-600);
}

.label {
    flex: 1 1 auto;
    min-width: 0;
    flex-direction: row;
    display: flex;
    align-items: center;
    justify-content: center;
}

.input::placeholder {
    color: var(--white-40);
}

.search_form_input.short {
    padding: 8px;
}

.search_form_input.short .input {
    position: absolute;
    width: 34px;
    height: 32px;
}

.search_form_input.short .icon {
    margin: 0;
}

.button_override {
    height: 34px;
}

.search_form_input.short .input::placeholder,
.search_form_input:not(.short) .input::placeholder {
    color: transparent;
}

@media (--desktop-small-plus) {
    .input {
        height: 36px;
    }
    .button_override {
        height: 44px;
    }
    .search_form_input.short {
        width: 52px;
        height: 52px;
    }
}

@media (--desktop-medium-plus) {
    .search_form_input.short {
        width: auto;
        padding: 8px 10px 8px 16px;
    }

    .search_form_input.short .input {
        width: calc(var(--digits) * 1ch);
        padding: 0;
        position: static;
        height: auto;
    }
    .search_form_input.short .input::placeholder {
        color: var(--white-40);
    }

    .search_form_input.short .icon {
        margin-right: 4px;
    }
}
</style>
